<template>
  <div class="shimmer-wrapper" :class="wrapperClass">
    <!-- Stats Card Shimmer -->
    <template v-if="type === 'stats-card'">
      <div v-for="i in count" :key="i" class="shimmer-stats-card">
        <div class="shimmer-card-icon shimmer-animate"></div>
        <div class="shimmer-card-content">
          <div class="shimmer-line shimmer-line-lg shimmer-animate"></div>
          <div class="shimmer-line shimmer-line-sm shimmer-animate"></div>
        </div>
      </div>
    </template>

    <!-- Dashboard Card Shimmer -->
    <template v-else-if="type === 'dashboard-card'">
      <div v-for="i in count" :key="i" class="shimmer-dashboard-card">
        <div class="shimmer-card-header">
          <div class="shimmer-line shimmer-line-md shimmer-animate" style="width: 60%"></div>
        </div>
        <div class="shimmer-card-body">
          <div class="shimmer-chart shimmer-animate"></div>
        </div>
      </div>
    </template>

    <!-- Table Row Shimmer -->
    <template v-else-if="type === 'table'">
      <div class="shimmer-table">
        <div class="shimmer-table-header">
          <div v-for="col in columns" :key="'header-'+col" 
               class="shimmer-table-cell shimmer-animate"
               :style="{ width: `${100/columns}%` }"></div>
        </div>
        <div v-for="row in count" :key="row" class="shimmer-table-row">
          <div v-for="col in columns" :key="'cell-'+row+'-'+col" 
               class="shimmer-table-cell shimmer-animate"
               :style="{ width: `${100/columns}%` }"></div>
        </div>
      </div>
    </template>

    <!-- Settings Card Shimmer -->
    <template v-else-if="type === 'settings-card'">
      <div v-for="i in count" :key="i" class="shimmer-settings-card">
        <div class="shimmer-icon-circle shimmer-animate"></div>
        <div class="shimmer-line shimmer-line-md shimmer-animate" style="width: 70%; margin-top: 15px"></div>
        <div class="shimmer-line shimmer-line-xs shimmer-animate" style="width: 90%; margin-top: 8px"></div>
        <div class="shimmer-line shimmer-line-xs shimmer-animate" style="width: 50%; margin-top: 15px"></div>
      </div>
    </template>

    <!-- Form Shimmer -->
    <template v-else-if="type === 'form'">
      <div class="shimmer-form">
        <div v-for="i in count" :key="i" class="shimmer-form-group">
          <div class="shimmer-label shimmer-animate"></div>
          <div class="shimmer-input shimmer-animate"></div>
        </div>
      </div>
    </template>

    <!-- Chart Shimmer -->
    <template v-else-if="type === 'chart'">
      <div class="shimmer-chart-container">
        <div class="shimmer-chart-title shimmer-animate"></div>
        <div class="shimmer-chart-area shimmer-animate"></div>
      </div>
    </template>

    <!-- List Item Shimmer -->
    <template v-else-if="type === 'list'">
      <div v-for="i in count" :key="i" class="shimmer-list-item">
        <div class="shimmer-avatar shimmer-animate"></div>
        <div class="shimmer-list-content">
          <div class="shimmer-line shimmer-line-md shimmer-animate" style="width: 80%"></div>
          <div class="shimmer-line shimmer-line-sm shimmer-animate" style="width: 60%"></div>
        </div>
      </div>
    </template>

    <!-- Quick Links Shimmer -->
    <template v-else-if="type === 'quick-links'">
      <div class="shimmer-quick-links">
        <div v-for="i in count" :key="i" class="shimmer-quick-link-btn shimmer-animate"></div>
      </div>
    </template>

    <!-- Text Lines Shimmer (default) -->
    <template v-else>
      <div class="shimmer-text">
        <div v-for="i in count" :key="i" 
             class="shimmer-line shimmer-animate"
             :class="[`shimmer-line-${size}`]"
             :style="{ width: getRandomWidth() }"></div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'ShimmerLoader',
  props: {
    type: {
      type: String,
      default: 'text',
      validator: (val) => ['text', 'stats-card', 'dashboard-card', 'table', 'settings-card', 'form', 'chart', 'list', 'quick-links'].includes(val)
    },
    count: {
      type: Number,
      default: 3
    },
    columns: {
      type: Number,
      default: 5
    },
    size: {
      type: String,
      default: 'md',
      validator: (val) => ['xs', 'sm', 'md', 'lg'].includes(val)
    },
    wrapperClass: {
      type: String,
      default: ''
    }
  },
  methods: {
    getRandomWidth() {
      const widths = ['100%', '90%', '80%', '70%', '95%', '85%'];
      return widths[Math.floor(Math.random() * widths.length)];
    }
  }
}
</script>

<style scoped>
/* Base Shimmer Animation */
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.shimmer-animate {
  background: linear-gradient(
    90deg,
    #f0f0f0 25%,
    #e8e8e8 37%,
    #f0f0f0 63%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
  border-radius: 4px;
}

/* Dark mode support */
[data-theme="dark"] .shimmer-animate,
.dark-mode .shimmer-animate {
  background: linear-gradient(
    90deg,
    #2d3748 25%,
    #374151 37%,
    #2d3748 63%
  );
  background-size: 200% 100%;
}

.shimmer-wrapper {
  width: 100%;
}

/* Stats Card */
.shimmer-stats-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
}

.shimmer-card-icon {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  flex-shrink: 0;
}

.shimmer-card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Dashboard Card */
.shimmer-dashboard-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
}

.shimmer-card-header {
  margin-bottom: 20px;
}

.shimmer-card-body {
  min-height: 200px;
}

.shimmer-chart {
  width: 100%;
  height: 200px;
  border-radius: 8px;
}

/* Table */
.shimmer-table {
  width: 100%;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
}

.shimmer-table-header {
  display: flex;
  background: #f8f9fa;
  padding: 12px 16px;
  gap: 12px;
}

.shimmer-table-row {
  display: flex;
  padding: 16px;
  gap: 12px;
  border-top: 1px solid #e9ecef;
}

.shimmer-table-cell {
  height: 18px;
  border-radius: 4px;
}

/* Settings Card */
.shimmer-settings-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 20px 16px;
  text-align: center;
  min-height: 180px;
}

.shimmer-icon-circle {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  margin: 0 auto;
}

/* Form */
.shimmer-form {
  padding: 16px 0;
}

.shimmer-form-group {
  margin-bottom: 20px;
}

.shimmer-label {
  width: 120px;
  height: 14px;
  margin-bottom: 8px;
}

.shimmer-input {
  width: 100%;
  height: 42px;
  border-radius: 8px;
}

/* Chart Container */
.shimmer-chart-container {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
}

.shimmer-chart-title {
  width: 40%;
  height: 20px;
  margin-bottom: 20px;
}

.shimmer-chart-area {
  width: 100%;
  height: 250px;
  border-radius: 8px;
}

/* List Item */
.shimmer-list-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #e9ecef;
}

.shimmer-list-item:last-child {
  border-bottom: none;
}

.shimmer-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
}

.shimmer-list-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Quick Links */
.shimmer-quick-links {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.shimmer-quick-link-btn {
  width: 140px;
  height: 45px;
  border-radius: 8px;
}

/* Line Sizes */
.shimmer-line {
  margin-bottom: 8px;
}

.shimmer-line:last-child {
  margin-bottom: 0;
}

.shimmer-line-xs {
  height: 10px;
}

.shimmer-line-sm {
  height: 14px;
}

.shimmer-line-md {
  height: 18px;
}

.shimmer-line-lg {
  height: 24px;
}

/* Grid Layout Helper */
.shimmer-wrapper.grid-4 {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.shimmer-wrapper.grid-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.shimmer-wrapper.grid-2 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

@media (max-width: 991px) {
  .shimmer-wrapper.grid-4,
  .shimmer-wrapper.grid-3 {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .shimmer-wrapper.grid-4,
  .shimmer-wrapper.grid-3,
  .shimmer-wrapper.grid-2 {
    grid-template-columns: 1fr;
  }
}
</style>
